import { lazy, Suspense, type FormEvent, useState } from 'react'
import './App.css'
import loginBg from './assets/login-bg.png'
import { GlobalLoading } from './features/dashboard/components/GlobalLoading'
import { authApi, systemApi, type ApiResponse, type LoginResult } from './services/api'
import { getAccessToken, setUserInfo } from './services/auth/token'
import { ApiError } from './services/http'

const Dashboard = lazy(() =>
  import('./features/dashboard/Dashboard').then((module) => ({
    default: module.Dashboard,
  })),
)

const FORCE_UPDATE_PWD_AFTER_LOGIN_KEY = 'forceUpdatePwdAfterLogin'
const FIRST_LOGIN_UPDATE_PWD_USER_ID_KEY = 'firstLoginUpdatePwdUserId'

interface CaptchaState {
  code: string
  rotations: number[]
  lines: Array<{
    x1: number
    y1: number
    x2: number
    y2: number
    opacity: number
  }>
  dots: Array<{
    cx: number
    cy: number
    r: number
  }>
}

function createCaptcha(): CaptchaState {
  const code = Math.floor(Math.random() * 10000).toString().padStart(4, '0')

  return {
    code,
    rotations: Array.from({ length: 4 }, () => Math.floor(Math.random() * 31) - 15),
    lines: Array.from({ length: 4 }, () => ({
      x1: Math.floor(Math.random() * 120),
      y1: Math.floor(Math.random() * 42),
      x2: Math.floor(Math.random() * 120),
      y2: Math.floor(Math.random() * 42),
      opacity: Number((Math.random() * 0.35 + 0.25).toFixed(2)),
    })),
    dots: Array.from({ length: 18 }, () => ({
      cx: Math.floor(Math.random() * 120),
      cy: Math.floor(Math.random() * 42),
      r: Number((Math.random() * 1.2 + 0.6).toFixed(1)),
    })),
  }
}

function App() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [captchaValue, setCaptchaValue] = useState('')
  const [captcha, setCaptcha] = useState(createCaptcha)
  const [isLoading, setIsLoading] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getAccessToken()))
  const [notice, setNotice] = useState('')
  const [noticeType, setNoticeType] = useState<'error' | 'success'>('error')

  function refreshCaptcha() {
    setCaptcha(createCaptcha())
    setCaptchaValue('')
  }

  async function onFinish(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setNotice('')

    if (!email.trim()) {
      showError('请输入您的电子邮箱')
      return
    }

    if (!password) {
      showError('请输入您的密码')
      return
    }

    if (!captchaValue) {
      showError('请输入验证码')
      return
    }

    if (captchaValue !== captcha.code) {
      showError('验证码错误，请重新输入')
      refreshCaptcha()
      return
    }

    setIsLoading(true)

    try {
      const result = await authApi.login({
        username: email.trim(),
        password,
        erpOrPos: 'erp',
      })

      if (result.code === 200) {
        syncFirstLoginState(result)
        await syncCurrentUserInfo(email.trim())
        setIsAuthenticated(true)
        setNoticeType('success')
        setNotice('登录成功')
        window.setTimeout(() => {
          window.location.href = '/public-page'
        }, 250)
        return
      }

      showError(result.msg || '登录失败，请重试')
      refreshCaptcha()
    } catch (error) {
      showError(getLoginErrorMessage(error))
      refreshCaptcha()
    } finally {
      setIsLoading(false)
    }
  }

  function showError(message: string) {
    setNoticeType('error')
    setNotice(message)
  }

  if (isAuthenticated) {
    return (
      <Suspense fallback={<div className="min-h-dvh bg-[#031724]"><GlobalLoading /></div>}>
        <Dashboard />
      </Suspense>
    )
  }

  return (
    <div
      className="login-page"
      style={{ backgroundImage: `url(${loginBg})` }}
    >
      <div className="login-page-inner">
        <div className="login-card">
          <div className="login-title-block">
            <h1>诺思智云ERP数据可视化大屏</h1>
          </div>

          <form className="login-form" name="SignUpForm" autoComplete="off" onSubmit={onFinish}>
            <div className="login-fields">
              <label className="form-item">
                <span className="sr-only">电子邮箱</span>
                <input
                  value={email}
                  autoComplete="username"
                  placeholder="电子邮箱 *"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>

              <label className="form-item password-item">
                <span className="sr-only">密码</span>
                <input
                  value={password}
                  type="password"
                  autoComplete="current-password"
                  placeholder="密码 *"
                  onChange={(event) => setPassword(event.target.value)}
                />
              </label>

              <div className="captcha-row">
                <label className="form-item captcha-field">
                  <span className="sr-only">验证码</span>
                  <input
                    value={captchaValue}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="验证码 *"
                    onChange={(event) => setCaptchaValue(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="captcha-button"
                  onClick={refreshCaptcha}
                  title="点击刷新验证码"
                >
                  <svg viewBox="0 0 120 42" className="captcha-svg" aria-label="验证码">
                    <defs>
                      <linearGradient id="captchaGradient" x1="0" x2="1" y1="0" y2="1">
                        <stop offset="0%" stopColor="#f4f8ff" />
                        <stop offset="100%" stopColor="#e8f0ff" />
                      </linearGradient>
                    </defs>
                    <rect width="120" height="42" fill="url(#captchaGradient)" />
                    {captcha.lines.map((line, index) => (
                      <line
                        key={`line-${index}`}
                        x1={line.x1}
                        y1={line.y1}
                        x2={line.x2}
                        y2={line.y2}
                        stroke={index % 2 ? '#1d4ed8' : '#7c3aed'}
                        strokeWidth="1"
                        opacity={line.opacity}
                      />
                    ))}
                    {captcha.dots.map((dot, index) => (
                      <circle
                        key={`dot-${index}`}
                        cx={dot.cx}
                        cy={dot.cy}
                        r={dot.r}
                        fill={index % 2 ? '#60a5fa' : '#a78bfa'}
                        opacity="0.55"
                      />
                    ))}
                    {captcha.code.split('').map((num, index) => (
                      <text
                        key={`${num}-${index}`}
                        x={18 + index * 24}
                        y={28}
                        textAnchor="middle"
                        fontSize="23"
                        fontWeight="700"
                        fontFamily="Arial, sans-serif"
                        fill={index % 2 ? '#2563eb' : '#7c3aed'}
                        transform={`rotate(${captcha.rotations[index]} ${18 + index * 24} 23)`}
                      >
                        {num}
                      </text>
                    ))}
                  </svg>
                </button>
              </div>

              {notice ? (
                <div className={`login-notice login-notice-${noticeType}`} role="alert">
                  {notice}
                </div>
              ) : null}

              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading ? '登录中...' : '登录'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

function syncFirstLoginState(result: ApiResponse<LoginResult>) {
  const resultWithFirst = result as ApiResponse<LoginResult> & { first?: 0 | 1 }
  const first = result.data?.first ?? resultWithFirst.first
  const userId = getUserId(result)
  const isFirstLogin = Number(first) === 1

  if (isFirstLogin) {
    sessionStorage.setItem(FORCE_UPDATE_PWD_AFTER_LOGIN_KEY, '1')
    if (userId) {
      sessionStorage.setItem(FIRST_LOGIN_UPDATE_PWD_USER_ID_KEY, String(userId))
    }
    return
  }

  sessionStorage.removeItem(FORCE_UPDATE_PWD_AFTER_LOGIN_KEY)
  sessionStorage.removeItem(FIRST_LOGIN_UPDATE_PWD_USER_ID_KEY)
}

function getUserId(source: ApiResponse<LoginResult>): string | number {
  return source.data?.userId ?? ''
}

async function syncCurrentUserInfo(username: string) {
  try {
    const userInfo = await systemApi.getCurrentUser()
    if (userInfo) {
      setUserInfo(JSON.stringify(userInfo))
      return
    }

    setUserInfo(JSON.stringify({ username, name: username, roles: [] }))
  } catch {
    setUserInfo(JSON.stringify({ username, name: username, roles: [] }))
  }
}

function getLoginErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message
  }

  return '登录失败，请重试'
}

export default App
